import {
  discountValidateResponseSchema,
  type DiscountValidateResponse,
} from '@repo/api/endpoints/discount';
import { loanAdd422ResponseSchema, type LoanAdd422Response } from '@repo/api/endpoints/loan';
import { env } from '@repo/env';

export type DiscountAppliedData = DiscountValidateResponse | LoanAdd422Response;

const STORAGE_KEY = 'promotionCode_discounts';

type DiscountStorageEntry = {
  code: string;
  response: DiscountAppliedData;
  writtenAt: number;
};

type DiscountStorageMap = Record<string, Record<string, DiscountStorageEntry>>;

function readStorageMap(): DiscountStorageMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};

    return JSON.parse(raw) as DiscountStorageMap;
  } catch {
    return {};
  }
}

function isEntryExpired(entry: DiscountStorageEntry): boolean {
  const ttl = env.VITE_PRAGMA_DISCOUNT_LOCAL_STORAGE_TTL;
  if (ttl === undefined) return false;

  return Date.now() - entry.writtenAt > ttl;
}

export type StoredDiscount = {
  code: string;
  response: DiscountAppliedData;
};

export function readDiscountFromStorage(userId: number, period: number): StoredDiscount | null {
  try {
    const map = readStorageMap();
    const entry = map[String(userId)]?.[String(period)];
    if (!entry) return null;

    if (isEntryExpired(entry)) {
      deleteDiscountForPeriod(userId, period);

      return null;
    }

    const discountParse = discountValidateResponseSchema.safeParse(entry.response);
    if (discountParse.success) return { code: entry.code, response: discountParse.data };

    const loanParse = loanAdd422ResponseSchema.safeParse(entry.response);
    if (loanParse.success) return { code: entry.code, response: loanParse.data };

    return null;
  } catch {
    return null;
  }
}

export function writeDiscountToStorage(
  userId: number,
  period: number,
  code: string,
  discount: DiscountAppliedData,
) {
  try {
    const map = readStorageMap();
    if (!map[String(userId)]) {
      map[String(userId)] = {};
    }
    map[String(userId)][String(period)] = {
      code,
      response: discount,
      writtenAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch (error) {
    console.error('Failed to write discount to localStorage', error);
  }
}

export function deleteDiscountForPeriod(userId: number, period: number) {
  try {
    const map = readStorageMap();
    if (!map[String(userId)]) return;
    delete map[String(userId)][String(period)];
    if (Object.keys(map[String(userId)]).length === 0) delete map[String(userId)];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch (error) {
    console.error('Failed to delete discount from localStorage', error);
  }
}

export function clearDiscountStorage() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear discount from localStorage', error);
  }
}
